import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import { fetchCategoryList } from "../../api/category";
import { fetchCheckList } from "../../api/check";
import { fetchPricelistList } from "../../api/pricelist";
import { buildLandingPricingPlans, type LandingPricingPlan } from "../../utils/landingPricing";
import { formatSiteAmount } from "../../utils/pricelistFormat";
import "./LandingPricing.scss";

export const LandingPricingSection = () => {
  const [plans, setPlans] = useState<LandingPricingPlan[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchPricelistList(), fetchCategoryList(), fetchCheckList()])
      .then(([pricelists, categories, checks]) => {
        if (cancelled) return;
        setPlans(buildLandingPricingPlans(pricelists, categories, checks));
        setError(null);
      })
      .catch(() => {
        if (!cancelled) {
          setPlans([]);
          setError("Не удалось загрузить тарифы");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const visiblePlans = useMemo(() => (ready ? plans : []), [plans, ready]);

  const primaryPrice = (plan: LandingPricingPlan) =>
    plan.prices.find((price) => price.isPrimary) ?? plan.prices[0];

  const secondaryPrices = (plan: LandingPricingPlan) =>
    plan.prices.filter((price) => !price.isPrimary);

  const skeletonCount = Math.max(visiblePlans.length, 1);

  return (
    <section id="pricing" className="landing-section landing-pricing-section">
      <div className="landing-container">
        <div className="landing-section-head center landing-pricing-head">
          <p className="landing-eyebrow">Тарифы</p>
          <h2 className="landing-section-title">Тарифы и стоимость</h2>
        </div>

        <div
          className={`landing-pricing-grid${!ready ? " landing-pricing-grid--loading" : ""}`}
          aria-busy={!ready}
        >
          {!ready ? (
            Array.from({ length: skeletonCount }, (_, index) => (
              <article key={`skeleton-${index}`} className="landing-pricing-card landing-pricing-card--skeleton" />
            ))
          ) : error ? (
            <p className="landing-pricing-empty landing-pricing-empty--error">{error}</p>
          ) : visiblePlans.length === 0 ? (
            <p className="landing-pricing-empty">Нет доступных тарифов</p>
          ) : (
            visiblePlans.map((plan) => {
              const mainPrice = primaryPrice(plan);
              const extras = secondaryPrices(plan);
              const showFeatures =
                plan.emailNotification || plan.scanByScheduleEnabled || plan.policyCheckEnabled;

              return (
                <article key={plan.id} className="landing-pricing-card">
                  {(plan.title || plan.description) && (
                    <div className="landing-pricing-card-top">
                      {plan.title && <h3 className="landing-pricing-card-title">{plan.title}</h3>}
                      {plan.description && (
                        <p className="landing-pricing-card-desc">{plan.description}</p>
                      )}
                    </div>
                  )}

                  {plan.prices.length > 0 && (
                    <div className="landing-pricing-price-block">
                      {mainPrice && (
                        <div className="landing-pricing-price-main">
                          <span className="landing-pricing-amount">{mainPrice.amount}</span>
                          {mainPrice.periodLabel ? (
                            <span className="landing-pricing-period">{mainPrice.periodLabel}</span>
                          ) : null}
                        </div>
                      )}
                      {extras.length > 0 && (
                        <div className="landing-pricing-price-alt">
                          {extras.map((price) => (
                            <span key={`${price.amount}-${price.periodLabel}`} className="landing-pricing-chip">
                              {price.amount}
                              {price.periodLabel ? ` ${price.periodLabel}` : ""}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {plan.siteAmount != null ? (
                    <div className="landing-pricing-sites">
                      <p className="landing-pricing-sites-label">Количество сайтов</p>
                      <p className="landing-pricing-sites-value">{formatSiteAmount(plan.siteAmount)}</p>
                    </div>
                  ) : null}

                  {showFeatures && (
                    <div className="landing-pricing-features">
                      <p className="landing-pricing-categories-label">Возможности</p>
                      <ul className="landing-pricing-categories-list">
                        {plan.emailNotification ? (
                          <li className="landing-pricing-category">
                            <span className="landing-pricing-category-icon" aria-hidden>
                              <Check size={14} strokeWidth={2} />
                            </span>
                            <span>Отправка отчёта на почту</span>
                          </li>
                        ) : null}
                        {plan.scanByScheduleEnabled ? (
                          <li className="landing-pricing-category">
                            <span className="landing-pricing-category-icon" aria-hidden>
                              <Check size={14} strokeWidth={2} />
                            </span>
                            <span>Запуск проверок по расписанию</span>
                          </li>
                        ) : null}
                        {plan.policyCheckEnabled ? (
                          <li className="landing-pricing-category">
                            <span className="landing-pricing-category-icon" aria-hidden>
                              <Check size={14} strokeWidth={2} />
                            </span>
                            <span>Проверка политики обработки ПД</span>
                          </li>
                        ) : null}
                      </ul>
                    </div>
                  )}

                  {plan.policyChecks.length > 0 && (
                    <div className="landing-pricing-categories">
                      <p className="landing-pricing-categories-label">Проверки политики обработки ПД</p>
                      <ul className="landing-pricing-categories-list">
                        {plan.policyChecks.map((check) => (
                          <li key={check.id} className="landing-pricing-category">
                            <span className="landing-pricing-category-icon" aria-hidden>
                              <Check size={14} strokeWidth={2} />
                            </span>
                            <span>{check.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {plan.policyLimits.length > 0 && (
                    <div className="landing-pricing-sites">
                      <p className="landing-pricing-sites-label">Лимиты политики обработки ПД</p>
                      <div className="landing-pricing-policy-limits">
                        {plan.policyLimits.map((row) => (
                          <div key={row.period} className="landing-pricing-policy-limit">
                            <span className="landing-pricing-policy-limit-period">{row.periodLabel}</span>
                            <span className="landing-pricing-policy-limit-value">{row.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {plan.checkGroups.length > 0 && (
                    <div className="landing-pricing-categories">
                      <p className="landing-pricing-categories-label">Проверки в тарифе</p>
                      <div className="landing-pricing-check-groups">
                        {plan.checkGroups.map((group) => (
                          <div key={group.id} className="landing-pricing-check-group">
                            <div className="landing-pricing-check-group-head">
                              <p className="landing-pricing-check-group-name">{group.name}</p>
                              {group.description ? (
                                <p className="landing-pricing-check-group-desc">{group.description}</p>
                              ) : null}
                            </div>
                            <ul className="landing-pricing-categories-list">
                              {group.checks.map((checkDescription) => (
                                <li key={checkDescription} className="landing-pricing-category">
                                  <span className="landing-pricing-category-icon" aria-hidden>
                                    <Check size={14} strokeWidth={2} />
                                  </span>
                                  <span>{checkDescription}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </article>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};
